import { useState, useEffect, FC, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import clsx from 'clsx';

import { IItemProps } from '../../../ProductPage/components/List/List';

import { useTypedSelector } from '../../../../../lib/hooks/useTypedSelector';
import Settings from '../../../../Icons/Settings';
import Arrow from '../../../../Icons/Arrow';
import Status from '../../../../Status/Status';
import Cart from '../../../ProductPage/Cart';
import Comment from '../../../../Icons/Comment';
import ModalAddToCart from '../../../../ModalAddToCart/ModalAddToCart';
import ModalComment from '../../../../ModalComment/ModalComment';

import { getDocumentUrl } from '../../../../../api/document/getDocumentUrl';

import styles from '../../SchemePage.module.scss';

const Item: FC<IItemProps> = ({
  name,
  article_number,
  cmmf,
  type,
  status,
  commercial_number,
  explodedViews,
  repInstruction,
  electricalScheme,
  techInfo,
  id,
  image,
  className,
  replacedBy,
  metaData,
}) => {
  const navigate = useNavigate();

  const { cart } = useTypedSelector((state) => state);
  const indexInCart = cart.findIndex((item) => item.product_id === id);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  const [isModalActive, setModalActive] = useState(false);

  const handleModalOpen = () => {
    setModalActive(true);
    document.body.style.overflow = 'hidden';
  };
  const handleModalClose = () => {
    setModalActive(false);
    document.body.style.overflow = 'auto';
  };

  const [commentVisible, setCommentVisible] = useState(false);
  const [isModalCommentActive, setModalCommentActive] = useState(false);
  const handleModalCommentOpen = () => {
    setModalCommentActive(true);
  };
  const handleModalCommentClose = () => {
    setModalCommentActive(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node))
        setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [electricalSchemeLink, setElectricalSchemeLink] = useState<
    null | string
  >(null);
  const [repInstructionLink, setRepInstructionLink] = useState<null | string>(
    null,
  );
  const [techInfoLink, setTechInfoLink] = useState<null | string>(null);
  const [explodedViewsLink, setExplodedViewsLink] = useState<null | string>(
    null,
  );

  useEffect(() => {
    if (
      electricalScheme &&
      electricalScheme.s3Bucket &&
      electricalScheme.s3Key
    ) {
      getDocumentUrl(electricalScheme.s3Bucket, electricalScheme.s3Key)
        .then((res) => {
          setElectricalSchemeLink(res.url);
        })
        .catch(() => {
          setElectricalSchemeLink(null);
        });
    }
    if (repInstruction && repInstruction.s3Bucket && repInstruction.s3Key) {
      getDocumentUrl(repInstruction.s3Bucket, repInstruction.s3Key)
        .then((res) => {
          setRepInstructionLink(res.url);
        })
        .catch(() => {
          setRepInstructionLink(null);
        });
    }
    if (techInfo && techInfo.s3Bucket && techInfo.s3Key) {
      getDocumentUrl(techInfo.s3Bucket, techInfo.s3Key)
        .then((res) => {
          setTechInfoLink(res.url);
        })
        .catch(() => {
          setTechInfoLink(null);
        });
    }
    if (explodedViews && explodedViews.s3Bucket && explodedViews.s3Key) {
      getDocumentUrl(explodedViews.s3Bucket, explodedViews.s3Key)
        .then((res) => {
          setExplodedViewsLink(res.url);
        })
        .catch(() => {
          setExplodedViewsLink(null);
        });
    }
  }, [electricalScheme, repInstruction, techInfo, explodedViews]);

  let replacedByItem =
    replacedBy?.length && Array.isArray(replacedBy)
      ? replacedBy.filter(
          (i) =>
            i.displayData.status &&
            i.displayData.status !== 'discontinued' &&
            i.displayData.isActive,
        )[0] || null
      : null;

  const in_stoke = status !== 'discontinued';

  return (
    <>
      <Link
        to={'/product/' + id}
        state={{ type: type }}
        className={clsx(styles.Item, className ? className : '')}
      >
        {type === 'model' ? (
          <>
            <span>{article_number}</span>
            <span>{commercial_number}</span>
            <span>{name}</span>
            <div>
              <div
                className={clsx(styles.select, { [styles.open]: isOpen })}
                ref={modalRef}
              >
                {electricalSchemeLink ||
                repInstructionLink ||
                techInfoLink ||
                explodedViewsLink ? (
                  <>
                    <span
                      className={styles.button}
                      onClick={(e) => {
                        e.preventDefault();
                        isOpen ? setIsOpen(false) : setIsOpen(true);
                      }}
                    >
                      <Settings />
                    </span>
                    <div className={styles.options}>
                      <ul>
                        {electricalSchemeLink && (
                          <li>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(electricalSchemeLink, '_blank');
                              }}
                            >
                              Взрыв-схема.pdf <span>(672 КБ)</span>
                            </button>
                          </li>
                        )}
                        {repInstructionLink && (
                          <li>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(repInstructionLink, '_blank');
                              }}
                            >
                              Руководство по ремонту.pdf <span>(5.4 МБ)</span>
                            </button>
                          </li>
                        )}
                        {techInfoLink && (
                          <li>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(techInfoLink, '_blank');
                              }}
                            >
                              Tехническая информация.pdf <span>(672 КБ)</span>
                            </button>
                          </li>
                        )}
                        {explodedViewsLink && (
                          <li>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(explodedViewsLink, '_blank');
                              }}
                            >
                              Номенклатура.pdf <span>(672 КБ)</span>
                            </button>
                          </li>
                        )}
                        {explodedViews &&
                          explodedViews.s3Bucket &&
                          explodedViews.s3Key && (
                            <a
                              className={styles.link_schema}
                              href={'/scheme/' + id}
                              onClick={(e) => {
                                e.preventDefault();
                                window.open('/scheme/' + id);
                              }}
                            >
                              Интерактивная взрыв-схема
                              <Arrow />
                            </a>
                          )}
                      </ul>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.type}>
              <img
                src={
                  type === 'model'
                    ? '/icons/model.png'
                    : type === 'part'
                      ? '/icons/detail.png'
                      : '/icons/accessory.png'
                }
                alt={type}
              />
            </div>
            <span className={styles.article}>
              <img
                src={
                  type === 'model'
                    ? '/icons/model.png'
                    : type === 'part'
                      ? '/icons/detail.png'
                      : '/icons/accessory.png'
                }
                alt={type}
                className={styles.type_mob}
              />
              {article_number}
            </span>
            <span className={styles.name}>{name}</span>
            <div className={styles.status_holder}>
              {status && <Status status={status || ''} list={true} />}
            </div>
            <div className={styles.button_holder_item}>
              {indexInCart === -1 ? (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleModalOpen();
                  }}
                  className={clsx({
                    [styles.no_stoke]:
                      !in_stoke && !replacedByItem ? true : false,
                  })}
                  disabled={!in_stoke && !replacedByItem ? true : false}
                >
                  <Cart />
                </button>
              ) : (
                <div className={styles.cart_holder}>
                  <button
                    className={clsx(styles.cart_btn)}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/cart');
                    }}
                  >
                    {cart[indexInCart].count} <Cart />
                  </button>
                  {cart[indexInCart].comment && (
                    <div
                      className={clsx(styles.comment, {
                        [styles.open]: commentVisible,
                      })}
                      onPointerEnter={() => setCommentVisible(true)}
                      onPointerLeave={() => setCommentVisible(false)}
                      onClick={() => handleModalCommentOpen()}
                    >
                      <Comment />
                      <p>{cart[indexInCart].comment}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </Link>
      {isModalCommentActive && (
        <ModalComment
          onClose={handleModalCommentClose}
          comment={cart[indexInCart].comment || ''}
        />
      )}
      {isModalActive && (
        <ModalAddToCart
          onClose={handleModalClose}
          data={{
            available:
              !metaData?.count && replacedByItem
                ? replacedByItem.metaData.count
                : metaData?.count,
            product_id:
              !metaData?.count && replacedByItem ? replacedByItem.id : id,
            name:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.name
                : name,
            status:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.status || ''
                : status || '',
            cmmf:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.cmmf
                : cmmf,
            article_number:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.partCode
                : article_number,
            type:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.type
                : type,
            image:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.image?.s3Key || ''
                : image?.s3Key || '',
            count: 1,
          }}
        />
      )}
    </>
  );
};

export default Item;
